// WO-102：公开数据唯一出口；服务端直读公开 API，浏览器走同源只读代理。
// 只依赖公开形状的纯类型；不会把 API 的服务、数据库或鉴权代码打包进门户。
import type {
  PortalChannelKey, PublicPortalProfile, PublicPortalChannel,
  PublicPortalCompleteItem, PublicPortalWithdrawnItem, PublicPortalJourneyStep
} from "../../../api/src/public-portal/public-portal.types";
export type { PortalChannelKey, PublicPortalProfile, PublicPortalJourneyStep };
export type PublicChannel = PublicPortalChannel & { readonly genres: readonly string[] };
export type PublicContentSummary = Omit<PublicPortalCompleteItem, "body" | "publicSource" | "organizationRefs" | "attachments">;
export type PublicContents = { readonly items: readonly PublicContentSummary[]; readonly nextCursor?: string };
export type PublicContent = PublicPortalCompleteItem | PublicPortalWithdrawnItem;
export type PublicTheme = { readonly theme: string; readonly count: number };
export type PublicFeedItem = { readonly title: string; readonly digest: string; readonly publicRef: string; readonly date: string; readonly channel: PortalChannelKey };
export type PublicScreen = {
  readonly profile: Pick<PublicPortalProfile, "name" | "syntheticNotice" | "snapshot">;
  readonly items: readonly PublicContentSummary[];
  readonly generatedAt: string;
  readonly etag: string;
};
export type PublicApiResult<T> =
  | { readonly status: "ok"; readonly data: T }
  | { readonly status: "unavailable"; readonly message: string; readonly reasonCode?: string }
  | { readonly status: "error"; readonly message: string; readonly httpStatus?: number };
// forwardedFor：服务端转发访客来源地址，公开 API 只信本机回环来的这一头，检索限流才按访客计数而不是全站共用一份。
export type PublicFetchOptions = { readonly signal?: AbortSignal; readonly revalidate?: number; readonly forwardedFor?: string | null };
export type ContentsQuery = { readonly channel?: PortalChannelKey; readonly genre?: string; readonly theme?: string; readonly cursor?: string; readonly limit?: number };
type Query = Readonly<Record<string, string | number | undefined>>;
const PUBLIC_PREFIX = "/api/v1/public/portal";
// 封闭路径清单防止代理变成内部接口或任意地址的转发器。
const PUBLIC_PATH = /^(profile|channels|contents|themes|search|feed|screen|contents\/[A-Za-z0-9_-]+|attachments\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/content)$/;

/** 仅由服务端读取。支持 API 原点、/api/v1 或完整公开门户前缀。 */
function serverBase(): string | null {
  const configured = process.env.PUBLIC_PORTAL_API_BASE_URL;
  if (!configured) return null;
  try {
    const url = new URL(configured);
    const pathname = url.pathname.replace(/\/+$/, "");
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password || url.search || url.hash || !["", "/api/v1", PUBLIC_PREFIX].includes(pathname)) return null;
    return `${url.origin}${PUBLIC_PREFIX}`;
  } catch { return null; }
}

// 只放地址字符（IPv4/IPv6、逗号、空格），超长或夹带别的字符就不转发。
const FORWARDED_FOR = /^[0-9A-Fa-f:., ]{1,200}$/;
function forwardedHeader(value: string | null | undefined): Record<string, string> {
  return value && FORWARDED_FOR.test(value) ? { "X-Forwarded-For": value } : {};
}

/** 供同源代理复用；固定 GET，不传 cookie、令牌或内部请求头（来源地址除外），不跟随重定向。 */
export async function fetchPublicPortalResponse(path: string, query: Query = {}, options: PublicFetchOptions = {}): Promise<Response> {
  if (!PUBLIC_PATH.test(path)) return Response.json({ message: "内容不存在或未公开" }, { status: 404 });
  const browser = typeof window !== "undefined";
  const base = browser ? PUBLIC_PREFIX : serverBase();
  if (!base) return Response.json({ message: "门户尚未开通" }, { status: 404 });
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
  const suffix = params.size ? `?${params}` : "";
  const live = browser || path.startsWith("attachments/") || path === "screen" || options.revalidate === 0;
  const init: RequestInit & { next?: { revalidate: number } } = {
    method: "GET", credentials: "omit", redirect: "error",
    headers: { Accept: "application/json", ...forwardedHeader(browser ? null : options.forwardedFor) },
    signal: options.signal ? AbortSignal.any([options.signal, AbortSignal.timeout(10_000)]) : AbortSignal.timeout(10_000),
    ...(live ? { cache: "no-store" as const } : { next: { revalidate: options.revalidate ?? 300 } })
  };
  return fetch(`${base}/${path}${suffix}`, init);
}

export function publicProxyResponse(path: string, upstream: Response): Response {
  const headers = new Headers({ "Content-Type": "application/json; charset=utf-8", "Cache-Control": "no-store", "X-Content-Type-Options": "nosniff" });
  if (/^attachments\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/content$/.test(path)) {
    for (const name of ["Content-Type", "Content-Disposition", "X-Content-Type-Options"]) {
      const value = upstream.headers.get(name);
      if (value !== null) headers.set(name, value);
    }
  }
  return new Response(upstream.body, { status: upstream.status, headers });
}

async function read<T>(path: string, query: Query = {}, options: PublicFetchOptions = {}): Promise<PublicApiResult<T>> {
  try {
    const response = await fetchPublicPortalResponse(path, query, options);
    if (response.status === 404) {
      const body = await response.json().catch(() => null) as { reasonCode?: unknown } | null;
      const reasonCode = typeof body?.reasonCode === "string" ? body.reasonCode : undefined;
      return { status: "unavailable", message: reasonCode === "NOT_IMPLEMENTED" ? "功能尚未开通" : "门户尚未开通", ...(reasonCode ? { reasonCode } : {}) };
    }
    if (!response.ok) return { status: "error", message: "公开内容暂时无法读取，请稍后重试。", httpStatus: response.status };
    return { status: "ok", data: await response.json() as T };
  } catch {
    return { status: "error", message: "公开内容暂时无法读取，请稍后重试。" };
  }
}

export const getProfile = (options?: PublicFetchOptions): Promise<PublicApiResult<PublicPortalProfile>> => read("profile", {}, options);
export const getChannels = (options?: PublicFetchOptions): Promise<PublicApiResult<readonly PublicChannel[]>> => read("channels", {}, options);
export const getContents = (query: ContentsQuery = {}, options?: PublicFetchOptions): Promise<PublicApiResult<PublicContents>> => read("contents", query, options);
export const getContent = (publicRef: string, options?: PublicFetchOptions): Promise<PublicApiResult<PublicContent>> => read(`contents/${encodeURIComponent(publicRef)}`, {}, options);
export const getThemes = (options?: PublicFetchOptions): Promise<PublicApiResult<readonly PublicTheme[]>> => read("themes", {}, options);
export const search = (q: string, limit?: number, options?: PublicFetchOptions): Promise<PublicApiResult<PublicContents>> => read("search", { q, limit }, options);
export const getFeed = (channel?: PortalChannelKey, options?: PublicFetchOptions): Promise<PublicApiResult<readonly PublicFeedItem[]>> => read("feed", { channel }, options);
export const getScreen = (options?: PublicFetchOptions): Promise<PublicApiResult<PublicScreen>> => read("screen", {}, options);
